import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Button,
  Divider,
  Alert,
  AlertTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked,
  TrendingUp as TrendingUpIcon,
  Business as BusinessIcon,
  Assessment as AssessmentIcon,
  Print as PrintIcon,
  AccountBalance as AccountBalanceIcon,
  Speed as SpeedIcon,
  AutoGraph as AutoGraphIcon,
  Security as SecurityIcon,
  Gavel as GavelIcon,
  Psychology as PsychologyIcon,
  IntegrationInstructions as IntegrationIcon,
  SmartToy as AIIcon,
  Timeline as TimelineIcon,
} from '@mui/icons-material';

const PortfolioManagementRoadmap: React.FC = () => {
  // Current portfolio state
  const currentPortfolios = [
    {
      name: 'Data Request Management',
      icon: <BusinessIcon />,
      budget: '$2.1M',
      projects: 5,
      status: 'Active',
      aiReadiness: 68,
      color: '#2196f3',
      keyFocus: [
        'SRMT enhancement and automation',
        'NIEM compliance automation',
        'Interagency data sharing',
        'Privacy impact assessment streamlining',
        'Expedite request intelligence',
      ],
    },
    {
      name: 'Data Governance',
      icon: <GavelIcon />,
      budget: '$2.8M',
      projects: 6,
      status: 'Active',
      aiReadiness: 72,
      color: '#4caf50',
      keyFocus: [
        'Data inventory and cataloging',
        'Enterprise data standards',
        'Quality by design framework',
        'Zero trust data security',
        'SMRT and NEIMS programs',
      ],
    },
    {
      name: 'Data Enterprise',
      icon: <IntegrationIcon />,
      budget: '$4.2M',
      projects: 8,
      status: 'Active',
      aiReadiness: 85,
      color: '#ff9800',
      keyFocus: [
        'Cloud infrastructure migration',
        'AI-powered pipeline automation',
        'Graph analytics platform',
        'Real-time stream processing',
        'Unified analytics platform',
      ],
    },
    {
      name: 'Data Product Management',
      icon: <TimelineIcon />,
      budget: '$1.9M',
      projects: 3,
      status: 'Active',
      aiReadiness: 58,
      color: '#9c27b0',
      keyFocus: [
        'Contract lifecycle automation',
        'Change management excellence',
        'Stakeholder engagement platform',
        'PMO efficiency optimization',
        'AI-powered contract analysis',
      ],
    },
  ];

  // Portfolio management enhancements
  const portfolioEnhancements = [
    {
      category: 'Executive Portfolio Dashboards',
      priority: 'Critical',
      features: [
        'Real-time portfolio health monitoring',
        'Cross-portfolio dependency tracking',
        'Investment optimization analytics',
        'Risk aggregation and visualization',
        'Strategic alignment scorecards',
        'Executive decision support tools',
      ],
      effort: 'Large',
      value: 'Very High',
      owner: 'Portfolio Management Office',
      unidatumAlignment: 'Business Intelligence & Analytics',
    },
    {
      category: 'AI-First Portfolio Strategy',
      priority: 'Critical',
      features: [
        'AI readiness assessment framework',
        'ML-powered resource allocation',
        'Predictive portfolio analytics',
        'Automated capacity planning',
        'AI innovation pipeline tracking',
        'GenAI integration roadmap',
      ],
      effort: 'Large',
      value: 'Very High',
      owner: 'OCDO - Advanced Analytics',
      unidatumAlignment: 'AI/ML Integration',
    },
    {
      category: 'Portfolio Risk Management',
      priority: 'High',
      features: [
        'Enterprise risk aggregation',
        'Cross-portfolio risk correlation',
        'Automated risk scoring and alerts',
        'Mitigation strategy tracking',
        'Compliance risk monitoring',
        'Financial risk assessment',
      ],
      effort: 'Medium',
      value: 'Very High',
      owner: 'Risk Management Team',
      unidatumAlignment: 'Risk & Compliance',
    },
    {
      category: 'Investment Optimization',
      priority: 'High',
      features: [
        'Portfolio value stream mapping',
        'ROI tracking and forecasting',
        'Budget optimization algorithms',
        'Resource reallocation recommendations',
        'Cost-benefit analysis automation',
        'Investment prioritization framework',
      ],
      effort: 'Medium',
      value: 'High',
      owner: 'Financial Planning Team',
      unidatumAlignment: 'Financial Analytics',
    },
    {
      category: 'Stakeholder Collaboration',
      priority: 'High',
      features: [
        'Executive collaboration portal',
        'Stakeholder engagement tracking',
        'Decision documentation system',
        'Meeting management automation',
        'Communication workflow optimization',
        'Feedback loop integration',
      ],
      effort: 'Medium',
      value: 'High',
      owner: 'Change Management Team',
      unidatumAlignment: 'Collaboration Tools',
    },
    {
      category: 'Performance Analytics',
      priority: 'High',
      features: [
        'KPI/OKR tracking dashboards',
        'Milestone achievement analytics',
        'Team utilization metrics',
        'Velocity and throughput analysis',
        'Quality metrics aggregation',
        'Predictive performance modeling',
      ],
      effort: 'Large',
      value: 'High',
      owner: 'Business Intelligence Branch',
      unidatumAlignment: 'Performance Management',
    },
    {
      category: 'Integration & Automation',
      priority: 'Medium',
      features: [
        'JIRA/Azure DevOps integration',
        'Financial system connectivity',
        'HR system integration for capacity',
        'Automated reporting pipelines',
        'API-driven portfolio updates',
        'Workflow automation engine',
      ],
      effort: 'Large',
      value: 'High',
      owner: 'Enterprise Architecture',
      unidatumAlignment: 'System Integration',
    },
  ];

  // Implementation roadmap
  const roadmapPhases = [
    {
      phase: 'Phase 1: Foundation & Visibility',
      timeline: '0-3 Months',
      focus: 'Portfolio Transparency & Baseline',
      items: [
        'Establish portfolio management framework',
        'Deploy executive dashboards for all 4 portfolios',
        'Implement real-time KPI/OKR tracking',
        'Create portfolio health scoring system',
        'Launch stakeholder collaboration portal',
        'Baseline AI readiness across portfolios',
      ],
      milestones: [
        'Executive dashboards operational',
        'Portfolio framework approved',
        'Baseline metrics established',
      ],
    },
    {
      phase: 'Phase 2: Integration & Intelligence',
      timeline: '3-6 Months',
      focus: 'Data Integration & AI Analytics',
      items: [
        'Integrate financial and HR systems',
        'Deploy AI-powered resource allocation',
        'Implement cross-portfolio dependency tracking',
        'Launch predictive analytics for portfolios',
        'Automate risk aggregation and scoring',
        'Deploy investment optimization tools',
      ],
      milestones: [
        'System integrations complete',
        'AI analytics operational',
        'Risk framework deployed',
      ],
    },
    {
      phase: 'Phase 3: Optimization & Automation',
      timeline: '6-9 Months',
      focus: 'Process Automation & Efficiency',
      items: [
        'Automate portfolio reporting pipelines',
        'Implement ML-based capacity planning',
        'Deploy automated budget optimization',
        'Launch workflow automation engine',
        'Implement decision support AI tools',
        'Optimize resource reallocation processes',
      ],
      milestones: [
        'Automation pipelines live',
        'ML capacity planning active',
        'Workflow engine deployed',
      ],
    },
    {
      phase: 'Phase 4: Strategic Excellence',
      timeline: '9-12 Months',
      focus: 'Strategic Alignment & Innovation',
      items: [
        'Deploy GenAI integration roadmap',
        'Implement strategic alignment scorecards',
        'Launch innovation pipeline tracking',
        'Deploy advanced predictive modeling',
        'Implement continuous improvement framework',
        'Establish portfolio center of excellence',
      ],
      milestones: [
        'GenAI roadmap deployed',
        'Strategic scorecards live',
        'Center of excellence established',
      ],
    },
  ];

  // Key metrics
  const portfolioMetrics = [
    {
      metric: 'Total Portfolio Investment',
      current: '$11.0M',
      target: '$12.5M',
      trend: 'up',
      description: 'Across 4 strategic portfolios',
    },
    {
      metric: 'Active Projects',
      current: '22',
      target: '25',
      trend: 'up',
      description: 'Enterprise-wide initiatives',
    },
    {
      metric: 'Average AI Readiness',
      current: '70.8%',
      target: '85%',
      trend: 'up',
      description: 'Portfolio AI maturity score',
    },
    {
      metric: 'Portfolio Health Score',
      current: '78%',
      target: '90%',
      trend: 'up',
      description: 'Composite health indicator',
    },
  ];

  return (
    <>
      {/* Print Styles */}
      <style>
        {`
          @media print {
            * {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
              color-adjust: exact !important;
            }
            @page {
              size: letter;
              margin: 0.5in;
            }
            body {
              margin: 0 !important;
              padding: 0 !important;
            }
            nav, .MuiDrawer-root, .MuiAppBar-root {
              display: none !important;
            }
            .MuiGrid-container {
              display: flex !important;
              flex-wrap: wrap !important;
            }
            .MuiGrid-item {
              flex-shrink: 0 !important;
            }
            .MuiGrid-grid-xs-12.MuiGrid-grid-md-3 {
              flex: 0 0 25% !important;
              max-width: 25% !important;
            }
            .MuiGrid-grid-xs-12.MuiGrid-grid-md-6 {
              flex: 0 0 50% !important;
              max-width: 50% !important;
            }
            .MuiGrid-grid-xs-12.MuiGrid-grid-md-4 {
              flex: 0 0 33.333% !important;
              max-width: 33.333% !important;
            }
            .MuiPaper-root, .MuiCard-root, .MuiChip-root, .MuiAlert-root, .MuiButton-root {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            .MuiTable-root {
              page-break-inside: auto !important;
            }
            .MuiTableRow-root {
              page-break-inside: avoid !important;
              break-inside: avoid !important;
            }
            .MuiCard-root, .MuiPaper-root {
              page-break-inside: avoid !important;
              break-inside: avoid !important;
            }
            .print-button {
              display: none !important;
            }
            .MuiBox-root {
              margin-top: 0 !important;
            }
          }
        `}
      </style>

      <Box sx={{ width: '100%', maxWidth: '1400px', mx: 'auto', mt: 4, mb: 4, px: { xs: 2, sm: 3, md: 4 } }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Box>
            <Typography variant="h4" gutterBottom sx={{ color: '#003366', fontWeight: 700 }}>
              MOC Portfolio Management Roadmap
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Strategic Portfolio Oversight & AI-First Transformation
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<PrintIcon />}
            onClick={() => window.print()}
            className="print-button"
            sx={{
              backgroundColor: '#003366',
              '&:hover': { backgroundColor: '#002244' },
            }}
          >
            Print to PDF
          </Button>
        </Box>

        {/* Executive Summary */}
        <Paper elevation={3} sx={{ p: 4, mb: 4, bgcolor: '#e3f2fd', border: '2px solid #1976d2' }}>
          <Typography variant="h5" gutterBottom sx={{ color: '#1976d2', fontWeight: 600 }}>
            Executive Summary
          </Typography>
          <Typography variant="body1" paragraph>
            MOC Portfolio Management encompasses four strategic portfolios totaling $11.0M in investment across 22 active projects. This roadmap outlines the transformation to AI-first portfolio management with advanced analytics, automated optimization, and executive decision support capabilities.
          </Typography>
          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={12} md={3}>
              <Card sx={{ height: '100%', bgcolor: '#4caf50', color: 'white' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h3" sx={{ fontWeight: 'bold' }}>4</Typography>
                  <Typography variant="body2">Strategic Portfolios</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card sx={{ height: '100%', bgcolor: '#2196f3', color: 'white' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h3" sx={{ fontWeight: 'bold' }}>22</Typography>
                  <Typography variant="body2">Active Projects</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card sx={{ height: '100%', bgcolor: '#ff9800', color: 'white' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h3" sx={{ fontWeight: 'bold' }}>$11M</Typography>
                  <Typography variant="body2">Total Investment</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card sx={{ height: '100%', bgcolor: '#9c27b0', color: 'white' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h3" sx={{ fontWeight: 'bold' }}>71%</Typography>
                  <Typography variant="body2">AI Readiness</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Paper>

        {/* Current Portfolio State */}
        <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 600, mt: 5, mb: 3 }}>
          Current Portfolio Landscape
        </Typography>

        <Grid container spacing={3} sx={{ mb: 5 }}>
          {currentPortfolios.map((portfolio, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Card sx={{ height: '100%', borderLeft: `4px solid ${portfolio.color}` }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ color: portfolio.color, mr: 2 }}>{portfolio.icon}</Box>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {portfolio.name}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                        <Chip
                          label={portfolio.status}
                          size="small"
                          sx={{ bgcolor: portfolio.color, color: 'white', fontWeight: 600 }}
                        />
                        <Chip
                          label={`${portfolio.projects} Projects`}
                          size="small"
                          variant="outlined"
                        />
                        <Chip
                          label={portfolio.budget}
                          size="small"
                          variant="outlined"
                        />
                      </Box>
                    </Box>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      AI Readiness: {portfolio.aiReadiness}%
                    </Typography>
                    <Box sx={{ width: '100%', bgcolor: '#e0e0e0', borderRadius: 1, height: 8 }}>
                      <Box
                        sx={{
                          width: `${portfolio.aiReadiness}%`,
                          bgcolor: portfolio.color,
                          height: 8,
                          borderRadius: 1,
                        }}
                      />
                    </Box>
                  </Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    Key Focus Areas:
                  </Typography>
                  <List dense>
                    {portfolio.keyFocus.map((focus, idx) => (
                      <ListItem key={idx} sx={{ py: 0.25 }}>
                        <ListItemIcon sx={{ minWidth: 28 }}>
                          <CheckCircleIcon fontSize="small" sx={{ color: portfolio.color }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={focus}
                          primaryTypographyProps={{ variant: 'body2' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Key Portfolio Metrics */}
        <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 600, mt: 5, mb: 3 }}>
          Key Portfolio Metrics
        </Typography>

        <Grid container spacing={3} sx={{ mb: 5 }}>
          {portfolioMetrics.map((metric, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h4" sx={{ color: '#003366', fontWeight: 'bold', mb: 1 }}>
                    {metric.current}
                  </Typography>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {metric.metric}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    {metric.description}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TrendingUpIcon fontSize="small" sx={{ color: '#4caf50' }} />
                    <Typography variant="caption" sx={{ color: '#4caf50', fontWeight: 600 }}>
                      Target: {metric.target}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Portfolio Management Enhancements */}
        <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 600, mt: 5, mb: 3 }}>
          Portfolio Management Enhancements
        </Typography>

        <Alert severity="info" sx={{ mb: 3 }}>
          <AlertTitle>Strategic Portfolio Transformation</AlertTitle>
          Transform MOC portfolio management with AI-first analytics, automated optimization, and executive decision support across all strategic portfolios.
        </Alert>

        <TableContainer component={Paper} sx={{ mb: 5 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#003366' }}>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Enhancement Area</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Priority</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Key Features</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Effort</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Value</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Owner</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {portfolioEnhancements.map((enhancement, index) => (
                <TableRow key={index} sx={{ '&:nth-of-type(odd)': { bgcolor: '#f5f5f5' } }}>
                  <TableCell>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {enhancement.category}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={enhancement.priority}
                      size="small"
                      color={
                        enhancement.priority === 'Critical'
                          ? 'error'
                          : enhancement.priority === 'High'
                          ? 'warning'
                          : 'default'
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <List dense sx={{ py: 0 }}>
                      {enhancement.features.slice(0, 3).map((feature, idx) => (
                        <ListItem key={idx} sx={{ py: 0, px: 0 }}>
                          <ListItemIcon sx={{ minWidth: 24 }}>
                            <RadioButtonUnchecked fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary={feature}
                            primaryTypographyProps={{ variant: 'body2' }}
                          />
                        </ListItem>
                      ))}
                      {enhancement.features.length > 3 && (
                        <Typography variant="caption" color="text.secondary" sx={{ pl: 3 }}>
                          +{enhancement.features.length - 3} more features
                        </Typography>
                      )}
                    </List>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={enhancement.effort}
                      size="small"
                      variant="outlined"
                      color={
                        enhancement.effort === 'Large'
                          ? 'error'
                          : enhancement.effort === 'Medium'
                          ? 'warning'
                          : 'success'
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={enhancement.value}
                      size="small"
                      sx={{
                        bgcolor:
                          enhancement.value === 'Very High'
                            ? '#4caf50'
                            : enhancement.value === 'High'
                            ? '#2196f3'
                            : '#ff9800',
                        color: 'white',
                        fontWeight: 600,
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {enhancement.owner}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Implementation Roadmap */}
        <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 600, mt: 5, mb: 3 }}>
          Implementation Roadmap: 12-Month Plan
        </Typography>

        <Grid container spacing={3} sx={{ mb: 5 }}>
          {roadmapPhases.map((phase, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Card
                sx={{
                  height: '100%',
                  borderLeft: `6px solid ${
                    index === 0
                      ? '#4caf50'
                      : index === 1
                      ? '#2196f3'
                      : index === 2
                      ? '#ff9800'
                      : '#9c27b0'
                  }`,
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: '#003366' }}>
                        {phase.phase}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Focus: {phase.focus}
                      </Typography>
                    </Box>
                    <Chip
                      label={phase.timeline}
                      sx={{
                        bgcolor:
                          index === 0
                            ? '#4caf50'
                            : index === 1
                            ? '#2196f3'
                            : index === 2
                            ? '#ff9800'
                            : '#9c27b0',
                        color: 'white',
                        fontWeight: 600,
                      }}
                    />
                  </Box>
                  <Divider sx={{ mb: 2 }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    Key Activities:
                  </Typography>
                  <List dense>
                    {phase.items.map((item, idx) => (
                      <ListItem key={idx} sx={{ py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <TrendingUpIcon fontSize="small" color="primary" />
                        </ListItemIcon>
                        <ListItemText
                          primary={item}
                          primaryTypographyProps={{ variant: 'body2' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    Key Milestones:
                  </Typography>
                  <List dense>
                    {phase.milestones.map((milestone, idx) => (
                      <ListItem key={idx} sx={{ py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <CheckCircleIcon fontSize="small" sx={{ color: '#4caf50' }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={milestone}
                          primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Expected Benefits */}
        <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 600, mt: 5, mb: 3 }}>
          Expected Benefits & Strategic Value
        </Typography>

        <Paper elevation={3} sx={{ p: 4, mb: 4, bgcolor: '#e8f5e9', border: '2px solid #4caf50' }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%', bgcolor: '#fff' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <SpeedIcon sx={{ fontSize: 40, color: '#2e7d32', mr: 2 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                      Operational Excellence
                    </Typography>
                  </Box>
                  <List dense>
                    <ListItem>
                      <ListItemText primary="40% faster portfolio decision-making" />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="60% reduction in reporting overhead" />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="50% improvement in resource utilization" />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%', bgcolor: '#fff' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <AIIcon sx={{ fontSize: 40, color: '#2e7d32', mr: 2 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                      AI-First Innovation
                    </Typography>
                  </Box>
                  <List dense>
                    <ListItem>
                      <ListItemText primary="85% AI readiness across portfolios" />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Predictive analytics for all projects" />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Automated optimization algorithms" />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%', bgcolor: '#fff' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <AutoGraphIcon sx={{ fontSize: 40, color: '#2e7d32', mr: 2 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                      Strategic Alignment
                    </Typography>
                  </Box>
                  <List dense>
                    <ListItem>
                      <ListItemText primary="Real-time strategic alignment tracking" />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Cross-portfolio synergy identification" />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Executive decision support tools" />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Paper>

        {/* Next Steps */}
        <Paper elevation={2} sx={{ p: 4, bgcolor: '#fff3e0', border: '2px solid #ff9800' }}>
          <Typography variant="h5" gutterBottom sx={{ color: '#ff9800', fontWeight: 600 }}>
            Recommended Next Steps
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon sx={{ color: '#ff9800' }} />
              </ListItemIcon>
              <ListItemText
                primary="1. Establish Portfolio Management Office (PMO)"
                secondary="Create dedicated PMO with executive sponsorship and cross-portfolio authority"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon sx={{ color: '#ff9800' }} />
              </ListItemIcon>
              <ListItemText
                primary="2. Deploy Executive Dashboards"
                secondary="Launch real-time portfolio health dashboards for all 4 strategic portfolios"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon sx={{ color: '#ff9800' }} />
              </ListItemIcon>
              <ListItemText
                primary="3. Implement AI Readiness Assessment"
                secondary="Conduct comprehensive AI readiness assessment across all portfolio projects"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon sx={{ color: '#ff9800' }} />
              </ListItemIcon>
              <ListItemText
                primary="4. Integrate Financial & HR Systems"
                secondary="Connect portfolio management with financial planning and human capital systems"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon sx={{ color: '#ff9800' }} />
              </ListItemIcon>
              <ListItemText
                primary="5. Launch Predictive Analytics"
                secondary="Deploy ML-powered predictive analytics for resource allocation and risk management"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon sx={{ color: '#ff9800' }} />
              </ListItemIcon>
              <ListItemText
                primary="6. Establish Success Metrics"
                secondary="Define KPIs for portfolio management effectiveness and AI transformation progress"
              />
            </ListItem>
          </List>
        </Paper>

        {/* Footer */}
        <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid #e0e0e0', textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            MOC Portfolio Management Office | Strategic Portfolio Roadmap
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Document Date: October 8, 2025 | Version 1.0
          </Typography>
        </Box>
      </Box>
    </>
  );
};

export default PortfolioManagementRoadmap;
